import { useCallback, useEffect, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import { Loader2, Check, X, HelpCircle, Mail, FileText, ExternalLink, Plus, ClipboardPaste, Upload, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Segmented from "@/components/Segmented";
import { readHeaders } from "@/estimating/ItbInboxPage";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import { pushUrl as navigate } from "@/estimating/lib/navigation";
import Hint from "@/components/Hint";

export const STATUS = {
  needs_review: ["Needs review", "bg-amber-100 text-amber-800"],
  screened: ["No Change", "bg-slate-100 text-slate-500"],
  reviewed: ["Reviewed", "bg-sky-100 text-sky-700"],
  co_created: ["CO created", "bg-emerald-100 text-emerald-700"],
  not_affected: ["No Change", "bg-slate-100 text-slate-500"],
  unmatched: ["No job matched", "bg-rose-100 text-rose-700"],
  pending_confirm: ["Pending confirm", "bg-amber-100 text-amber-800"],
  unmatched_return: ["Unmatched return", "bg-rose-100 text-rose-700"],
  linked: ["Linked to revision", "bg-emerald-100 text-emerald-700"],
};
export const AffectsMark = ({ value }) => {
  const [Icon, cls, title] = value === "yes" ? [Check, "text-emerald-600", "Affects our scopes"]
    : value === "no" ? [X, "text-rose-600", "Does not affect our scopes"]
      : [HelpCircle, "text-amber-500", "Scanner unsure — needs a human look"];
  return <span title={title} className="inline-flex"><Icon className={`h-4 w-4 ${cls}`} /></span>;
};
export const TIER = {
  A: ["A", "In Contract — touches a scope, product, room or manufacturer we hold on this job", "bg-emerald-600 text-white"],
  B: ["B", "Not in Contract — a scope ABS does, or an exclusion, that is not on this job", "bg-orange-500 text-white"],
  C: ["C", "Adjacent — only adjacent-trade wording; could bump our work", "bg-amber-200 text-amber-900"],
  D: ["D", "No Change — confident it does not affect our scopes", "bg-slate-200 text-slate-600"],
};
export const TierBadge = ({ tier }) => {
  const [t, title, cls] = TIER[tier] || ["—", "Not screened against a job yet", "bg-slate-100 text-slate-400"];
  return <span title={title} className={`inline-flex h-5 w-5 items-center justify-center rounded text-[11px] font-bold ${cls}`} data-tier={tier || ""}>{t}</span>;
};
const LANES = [
  ["A", "In Contract", (i) => i.tier === "A" && i.status !== "not_affected"],
  ["B", "Not in Contract", (i) => i.tier === "B" && i.status !== "not_affected"],
  ["C", "Adjacent", (i) => i.tier === "C" && i.status !== "not_affected"],
  ["D", "No Change", (i) => i.doc_type !== "submittal_return" && (i.status === "not_affected" || i.status === "screened" || i.tier === "D")],
  ["S", "Submittal return", (i) => i.doc_type === "submittal_return"],
];
export const StatusPill = ({ status }) => {
  const [label, cls] = STATUS[status] || [status, "bg-slate-100 text-slate-600"];
  return <span className={`rounded-full px-2 py-0.5 text-[11px] ${cls}`}>{label}</span>;
};

/** Jobs › Intake — every RFI / COR / PCO / ASI / CCD the door received for this job. */
export default function IntakeTab({ project }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("A");
  const [openParam, setOpenParam] = useUrlState("intake", "");
  const [open, setOpenState] = useState(null);
  const setOpen = (it) => { setOpenState(it); setOpenParam(it ? it.id : ""); };
  useEffect(() => {
    if (openParam && !open && data?.items) {
      const it = data.items.find((i) => i.id === openParam);
      if (it) setOpenState(it);
    }
  }, [openParam, data]); // eslint-disable-line react-hooks/exhaustive-deps
  const [paste, setPaste] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      list.forEach((f) => form.append("files", f));
      form.append("project_id", project.id);
      const r = await opsApi.intakeUpload(form);
      toast.success(r.items.map((i) => `${i.gc_number || i.doc_type.toUpperCase()} — ${STATUS[i.status]?.[0] || i.status}`).join(" · "));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read that file"); }
    finally { setUploading(false); }
  };

  const load = useCallback(async () => {
    try { setData(await opsApi.projectIntake(project.id)); }
    catch { toast.error("Could not load the intake log"); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex items-center gap-2 py-16 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading intake…</div>;
  const lane = LANES.find(([k]) => k === view) || LANES[0];
  const rows = data.items.filter(lane[2]);

  return (
    <div className="space-y-4" data-testid="ops-intake-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented value={view} onChange={setView}
                   options={LANES.map(([k, label, fn]) => {
                     const n = data.items.filter(fn).length;
                     return { key: k, label: n ? `${label} · ${n}` : label, testId: `ops-intake-lane-${k}` };
                   })} />
        <Button size="sm" className="ml-auto h-7 px-2 text-xs bg-[#007BFF] hover:bg-[#0069d9] text-white" onClick={() => setPaste(true)}
                data-testid="ops-intake-paste" title="Paste text">
          <ClipboardPaste className="h-3.5 w-3.5" />
        </Button>
        <label className="inline-flex h-7 cursor-pointer items-center rounded-md bg-[#007BFF] px-2 text-white hover:bg-[#0069d9]"
               title="Upload document" data-testid="ops-intake-upload">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          <input type="file" multiple accept=".pdf,.eml,.txt,image/*" className="hidden" data-testid="ops-intake-upload-input"
                 onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </label>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[900px] text-sm" data-testid="ops-intake-log">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-center"><Hint text="A In Contract · B Not in Contract · C Adjacent · D No Change">Tier</Hint></th>
              <th className="px-3 py-2 text-left">Document</th>
              <th className="px-3 py-2 text-left">Subject</th>
              <th className="px-3 py-2 text-left">From</th>
              <th className="px-3 py-2 text-left">Received</th>
              <th className="px-3 py-2 text-center"><Hint text="Does it affect our scopes?">Affects</Hint></th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">CO</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">{view === "D" ? "Nothing here yet — documents the screen is confident do not touch our scopes, and anything a reviewer marked No Change, land here and are never deleted." : "Nothing in this lane."}</td></tr>}
            {rows.map((i) => (
              <tr key={i.id} onClick={() => setOpen(i.id)} className="cursor-pointer hover:bg-slate-50" data-testid={`ops-intake-row-${i.id}`}>
                <td className="px-3 py-2 text-center"><TierBadge tier={i.tier} />{i.flags?.includes("raised_on_rescreen") && <span className="ml-1 text-[10px] text-sky-600" title="Raised on re-screen after the job's FRS grew">↑</span>}</td>
                <td className="px-3 py-2 font-medium">{i.label}</td>
                <td className="max-w-[360px] truncate px-3 py-2" title={i.subject}>{i.subject}</td>
                <td className="px-3 py-2 text-xs">{i.from_name || i.from_email}</td>
                <td className="px-3 py-2 text-xs">{(i.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-center"><AffectsMark value={i.affects} /></td>
                <td className="px-3 py-2"><StatusPill status={i.status} /></td>
                <td className="px-3 py-2 font-mono text-xs">{i.co_number || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <IntakeDrawer itemId={open} project={project} onClose={() => setOpen(null)} onChanged={load} />}
      {paste && <PasteDialog projectId={project.id} onClose={() => setPaste(false)} onDone={load} />}
    </div>
  );
}

export function IntakeDrawer({ itemId, project, onClose, onChanged }) {
  const [it, setIt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { opsApi.intakeDetail(itemId).then(setIt).catch(() => toast.error("Could not open that item")); }, [itemId]);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await onChanged(); setIt(await opsApi.intakeDetail(itemId)); }
    catch (e) { toast.error(e?.response?.data?.detail || "That did not work"); }
    finally { setBusy(false); }
  };
  const openCo = () => navigate(`/jobs?job=${it.project_id}&otab=change_orders&opsco=${it.change_order_id}`);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l border-border bg-card p-5 text-card-foreground shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-intake-drawer">
        {!it ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <TierBadge tier={it.tier} />
              <h3 className="text-base font-semibold">{it.label}</h3>
              <StatusPill status={it.status} />
              {(it.flags || []).map((f) => <span key={f} className="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] text-slate-500">{f.replace(/_/g, " ")}</span>)}
              <AffectsMark value={it.affects} />
              {it.due_date && <span className="text-xs text-slate-500">due {it.due_date}</span>}
              <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-sm font-medium">{it.subject}</p>
            <p className="text-xs text-slate-500">{it.from_name} &lt;{it.from_email}&gt; · {(it.created_at || "").slice(0, 16).replace("T", " ")} · parsed by {it.parser}</p>

            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs dark:bg-slate-900/40" data-testid="ops-intake-scan">
              <p className="font-semibold uppercase tracking-wide text-slate-500">Scope scan</p>
              <p className="mt-1">{it.affects_reason || "—"}</p>
              {it.affects_scopes?.length > 0 && <p className="mt-1 text-slate-500">Scopes: {it.affects_scopes.join(", ")} · confidence {Math.round((it.affects_confidence || 0) * 100)}%</p>}
            </div>
            {it.hits?.length > 0 && (
              <div className="mt-3 rounded-lg border border-slate-200 p-3 text-xs" data-testid="ops-intake-why">
                <p className="font-semibold uppercase tracking-wide text-slate-500">Why flagged · dictionary v{it.dictionary_version}</p>
                {Object.entries(it.hits.reduce((m, h) => { (m[h.section] = m[h.section] || []).push(h); return m; }, {})).map(([sec, hs]) => (
                  <div key={sec} className="mt-2">
                    <p className="font-medium text-slate-700 dark:text-slate-200">{sec.replace(/_/g, " ")}</p>
                    <ul className="mt-0.5 space-y-0.5 text-slate-600 dark:text-slate-300">
                      {hs.slice(0, 6).map((h, i) => <li key={i}><b>{h.term}</b> — <span className="text-slate-500">…{h.snippet}…</span></li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {it.learned_terms?.length > 0 && (
              <p className="mt-2 text-[11px] text-slate-500" data-testid="ops-intake-learned">
                Screen learned from this review: {it.learned_terms.join(", ")} → {it.status === "not_affected" ? "No Change" : "affects us"}. After 3 No Change reviews with no "affects", a term stops lifting documents into Adjacent.
              </p>
            )}
            {it.summary && <div className="mt-3 text-sm"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Summary</p><p className="mt-1">{it.summary}</p></div>}
            {it.question && <div className="mt-3 text-sm"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Question / directive</p><p className="mt-1 whitespace-pre-wrap">{it.question}</p></div>}
            {it.references?.length > 0 && <p className="mt-2 text-xs text-slate-500">References: {it.references.join(" · ")}</p>}

            <div className="mt-4 flex flex-wrap gap-2" data-testid="ops-intake-actions">
              {it.change_order_id ? (
                <Button size="sm" onClick={openCo} data-testid="ops-intake-open-co"><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open change order</Button>
              ) : it.project_id && (
                <Button size="sm" disabled={busy} data-testid="ops-intake-create-co"
                        onClick={() => act(() => opsApi.intakeCreateCo(it.id), "Draft change order created")}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Create CO
                </Button>
              )}
              {it.status !== "not_affected" ? (
                <Button size="sm" variant="outline" disabled={busy} data-testid="ops-intake-not-affected"
                        title="Confident this does not affect our scopes — the screen learns from it"
                        onClick={() => { const note = window.prompt("Why is this No Change? (logged with the record — the screen learns which adjacent wording never affects us)"); if (note && note.trim()) act(() => opsApi.intakeReview(it.id, { action: "not_affected", note }), "Logged as No Change"); }}>
                  <X className="mr-1.5 h-3.5 w-3.5" /> No Change
                </Button>
              ) : (
                <Button size="sm" variant="outline" disabled={busy} data-testid="ops-intake-affects"
                        onClick={() => act(() => opsApi.intakeReview(it.id, { action: "affects" }), "Flagged as affecting us")}>
                  <Check className="mr-1.5 h-3.5 w-3.5" /> It affects us
                </Button>
              )}
              {it.status === "needs_review" && (
                <Button size="sm" variant="ghost" disabled={busy} data-testid="ops-intake-reviewed"
                        onClick={() => act(() => opsApi.intakeReview(it.id, { action: "reviewed" }), "Marked reviewed")}>Mark reviewed</Button>
              )}
            </div>

            {it.documents?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Filed documents</p>
                <ul className="mt-1 space-y-1 text-xs" data-testid="ops-intake-docs">
                  {it.documents.map((d) => (
                    <li key={d.id}><button type="button" className="inline-flex items-center gap-1 hover:underline" data-testid={`ops-intake-doc-${d.id}`}
                            onClick={() => opsApi.openDocument(d.id).catch(() => toast.error("Could not open that file"))}><FileText className="h-3 w-3" /> {d.file_name}</button></li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4">
              <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500"><Mail className="h-3 w-3" /> Email</p>
              <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700 dark:bg-slate-900 dark:text-slate-200">{it.body}</pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PasteDialog({ projectId = "", onClose, onDone }) {
  const [f, setF] = useState({ subject: "", from_name: "", from_email: "", body: "" });
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const found = readHeaders(f.body);
    const payload = { ...f, ...Object.fromEntries(Object.entries(found).filter(([k]) => !f[k])) };
    if (!payload.subject) payload.subject = (f.body.split("\n").find((l) => l.trim()) || "Pasted email").slice(0, 200);
    setBusy(true);
    try {
      let r;
      if (docs.length) {
        const form = new FormData();
        docs.forEach((d) => form.append("files", d));
        Object.entries({ ...payload, project_id: projectId }).forEach(([k, v]) => form.append(k, v || ""));
        r = (await opsApi.intakeUpload(form)).items[0];
      } else {
        r = await opsApi.intakeManual({ ...payload, project_id: projectId });
      }
      toast.success(`Read as ${r.label || r.doc_type.toUpperCase()} — ${STATUS[r.status]?.[0] || r.status}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read that email"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="ops-intake-paste-dialog">
        <DialogHeader><DialogTitle>Paste an email</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Paste a forwarded RFI or change document; it is classified, scanned and filed automatically.</DialogDescription>
        <div className="space-y-2">
          <textarea rows={16} value={f.body}
                    placeholder="Paste the whole email here — including the From/Subject lines and signature. We read the sender, what the document is (RFI, PCO, COR, ASI, CCD…), its number, and whether it touches our scopes."
                    onPaste={(e) => {
                      const text = e.clipboardData?.getData("text") || "";
                      const found = readHeaders(text);
                      if (Object.keys(found).length) setF((p) => ({ ...p, ...found, body: text }));
                    }}
                    onChange={(e) => setF({ ...f, body: e.target.value })}
                    className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs leading-relaxed focus:border-[#007BFF] focus:outline-none dark:border-slate-700 dark:bg-slate-900"
                    data-testid="ops-intake-paste-body" />
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="ops-intake-paste-docs">
            <label className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-md border border-slate-200 px-2 text-slate-600 hover:border-[#007BFF] hover:text-[#007BFF] dark:border-slate-700"
                   title="Attach the GC's PDF or photo — the parser reads it together with the email">
              <Upload className="h-3.5 w-3.5" /> Add document
              <input type="file" multiple accept=".pdf,image/*,.xlsx,.csv,.txt" className="hidden" data-testid="ops-intake-paste-docs-input"
                     onChange={(e) => { setDocs((d) => [...d, ...Array.from(e.target.files || [])]); e.target.value = ""; }} />
            </label>
            {docs.map((d, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                <FileText className="h-3 w-3" /> {d.name}
                <button type="button" onClick={() => setDocs((x) => x.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <p className="text-[10px] text-slate-400">One paste is enough; add the document and both are read together. Affecting items are filed, flagged for the PM &amp; PE and, for change documents, opened as a draft CO. Nothing is emailed out.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ops-intake-paste-cancel">Cancel</Button>
          <Button onClick={go} disabled={busy || (!f.body.trim() && !docs.length)} data-testid="ops-intake-paste-go">
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />} Read it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
